import React, {useState, useEffect} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useDatabase} from '../context/DatabaseContext';
import {useToast} from '../context/ToastContext';
import {useUserNickname} from '../hooks/useUserNickname';
import {colors} from '../theme/colors';

const UNIT_CONVERSIONS = {
  grams: 1,           // Base unit
  teaspoon: 5,        // 1 tsp = 5g (little spoon)
  tablespoon: 15,     // 1 tbsp = 15g (spoon)
};

const HomeScreen = () => {
  const {getAllFoods, addFood, addEntry} = useDatabase();
  const {success, error} = useToast();
  const userNickname = useUserNickname();
  const [searchParams] = useSearchParams();
  const [foods, setFoods] = useState([]);
  const [selectedFood, setSelectedFood] = useState(null);
  const [amount, setAmount] = useState('');
  const [unit, setUnit] = useState('grams');
  const [showAddFoodModal, setShowAddFoodModal] = useState(false);
  const [newFoodName, setNewFoodName] = useState('');
  const [newFoodCalories, setNewFoodCalories] = useState('');
  const [newFoodProtein, setNewFoodProtein] = useState('');
  const [newFoodCarbs, setNewFoodCarbs] = useState('');
  const [newFoodFat, setNewFoodFat] = useState('');
  const [newFoodBaseAmount, setNewFoodBaseAmount] = useState('1');
  const [newFoodBaseAmountUnit, setNewFoodBaseAmountUnit] = useState('unit');

  useEffect(() => {
    loadFoods();
  }, []);

  useEffect(() => {
    const foodIdParam = searchParams.get('foodId');
    if (foodIdParam && foods.length > 0) {
      const foodId = parseInt(foodIdParam, 10);
      if (!isNaN(foodId) && foods.find(f => f.id === foodId)) {
        setSelectedFood(foodId);
      }
    }
  }, [searchParams, foods]);

  const loadFoods = async () => {
    try {
      const foodsList = await getAllFoods();
      const foodsArray = foodsList || [];
      setFoods(foodsArray);
      if (foodsArray.length > 0 && !selectedFood) {
        setSelectedFood(foodsArray[0].id);
      }
    } catch (error) {
      console.error('Error loading foods:', error);
      setFoods([]);
    }
  };

  const convertToGrams = (amount, unit) => {
    return amount * UNIT_CONVERSIONS[unit];
  };

  // Check if food uses new schema (base portion) or old schema (per 100g)
  const isBasePortionFood = (food) => {
    return food.base_amount !== undefined && 
           food.base_amount_unit !== undefined &&
           food.calories !== undefined;
  };

  // Calculate nutrition for old schema foods (per 100g)
  const calculateNutrition = (food, grams) => {
    const ratio = grams / 100;
    return {
      calories: ((food.calories_per_100g || 0) * ratio).toFixed(1),
      protein: ((food.protein_per_100g || 0) * ratio).toFixed(1),
      carbs: ((food.carbs_per_100g || 0) * ratio).toFixed(1),
      fat: ((food.fat_per_100g || 0) * ratio).toFixed(1),
    };
  };

  // Get Hebrew unit label
  const getHebrewUnitLabel = (unit, amount = 1) => {
    if (unit === 'unit') {
      return amount === 1 ? 'יחידה' : 'יחידות';
    } else if (unit === 'gram') {
      return 'גרם';
    } else if (unit === 'tablespoon') {
      return amount === 1 ? 'כף' : 'כפות';
    } else if (unit === 'teaspoon') {
      return amount === 1 ? 'כפית' : 'כפיות';
    }
    return unit;
  };

  // Get display name with portion info
  const getFoodDisplayName = (food) => {
    if (isBasePortionFood(food)) {
      let portionLabel = '';
      if (food.base_amount_unit === 'unit') {
        portionLabel = `${food.base_amount} ${getHebrewUnitLabel('unit', food.base_amount)}`;
      } else if (food.base_amount_unit === 'gram') {
        portionLabel = `${food.base_amount} ${getHebrewUnitLabel('gram')}`;
      } else if (food.base_amount_unit === 'tablespoon') {
        portionLabel = `${food.base_amount} ${getHebrewUnitLabel('tablespoon', food.base_amount)}`;
      }
      return `${food.name} (${portionLabel})`;
    }
    return food.name;
  };

  const handleAddEntry = async () => {
    if (!selectedFood || !amount || parseFloat(amount) <= 0) {
      error('אנא בחר מזון והכנס כמות תקינה');
      return;
    }

    const food = foods.find(f => f.id === selectedFood);
    if (!food) {
      error('מזון שנבחר לא נמצא');
      return;
    }

    let grams = 0;
    let nutrition = null;

    // Handle new schema (base portion foods)
    if (isBasePortionFood(food)) {
      const amountValue = parseFloat(amount);
      
      if (food.base_amount_unit === 'unit') {
        // For unit-based foods, amount is in units
        const portions = amountValue / food.base_amount;
        nutrition = {
          calories: (food.calories * portions).toFixed(1),
          protein: (food.protein * portions).toFixed(1),
          carbs: (food.carbs * portions).toFixed(1),
          fat: (food.fat * portions).toFixed(1),
        };
        // Estimate grams for display (approximate)
        grams = amountValue * 100; // Rough estimate, will be stored but not used for calculation
      } else if (food.base_amount_unit === 'gram') {
        // For gram-based foods, convert to grams and calculate
        grams = convertToGrams(amountValue, unit);
        const portions = grams / food.base_amount;
        nutrition = {
          calories: (food.calories * portions).toFixed(1),
          protein: (food.protein * portions).toFixed(1),
          carbs: (food.carbs * portions).toFixed(1),
          fat: (food.fat * portions).toFixed(1),
        };
      } else if (food.base_amount_unit === 'tablespoon') {
        // For tablespoon-based foods, convert to tablespoons
        const gramsValue = convertToGrams(amountValue, unit);
        const tbspValue = gramsValue / 15; // 1 tbsp = 15g
        const portions = tbspValue / food.base_amount;
        nutrition = {
          calories: (food.calories * portions).toFixed(1),
          protein: (food.protein * portions).toFixed(1),
          carbs: (food.carbs * portions).toFixed(1),
          fat: (food.fat * portions).toFixed(1),
        };
        grams = gramsValue;
      }
    } else {
      // Old schema: per 100g
      grams = convertToGrams(parseFloat(amount), unit);
      nutrition = calculateNutrition(food, grams);
    }

    const entrySuccess = await addEntry(
      selectedFood,
      grams,
      parseFloat(nutrition.calories),
      parseFloat(nutrition.protein),
      parseFloat(nutrition.carbs),
      parseFloat(nutrition.fat),
    );

    if (entrySuccess) {
      success('רשומה נוספה בהצלחה!');
      setAmount('');
    } else {
      error('נכשל בהוספת רשומה');
    }
  };

  const handleAddFood = async () => {
    if (
      !newFoodName ||
      !newFoodCalories ||
      !newFoodProtein ||
      !newFoodCarbs ||
      !newFoodFat ||
      !newFoodBaseAmount ||
      !newFoodBaseAmountUnit
    ) {
      error('אנא מלא את כל השדות');
      return;
    }

    const foodSuccess = await addFood(
      newFoodName,
      parseFloat(newFoodCalories),
      parseFloat(newFoodProtein),
      parseFloat(newFoodCarbs),
      parseFloat(newFoodFat),
      parseFloat(newFoodBaseAmount),
      newFoodBaseAmountUnit,
    );

    if (foodSuccess) {
      success('מזון נוסף בהצלחה!');
      setShowAddFoodModal(false);
      setNewFoodName('');
      setNewFoodCalories('');
      setNewFoodProtein('');
      setNewFoodCarbs('');
      setNewFoodFat('');
      setNewFoodBaseAmount('1');
      setNewFoodBaseAmountUnit('unit');
      loadFoods();
    } else {
      error('נכשל בהוספת מזון - ייתכן שהמזון כבר קיים במאגר');
    }
  };

  const selectedFoodData = foods.find(f => f.id === selectedFood);
  
  // Calculate preview nutrition with proper handling for base portion foods
  const previewNutrition = (() => {
    if (!selectedFoodData || !amount || parseFloat(amount) <= 0) {
      return null;
    }
    
    const amountValue = parseFloat(amount);
    
    if (isBasePortionFood(selectedFoodData)) {
      if (selectedFoodData.base_amount_unit === 'unit') {
        const portions = amountValue / selectedFoodData.base_amount;
        return {
          calories: (selectedFoodData.calories * portions).toFixed(1),
          protein: (selectedFoodData.protein * portions).toFixed(1),
          carbs: (selectedFoodData.carbs * portions).toFixed(1),
          fat: (selectedFoodData.fat * portions).toFixed(1),
        };
      } else if (selectedFoodData.base_amount_unit === 'gram') {
        const grams = convertToGrams(amountValue, unit);
        const portions = grams / selectedFoodData.base_amount;
        return {
          calories: (selectedFoodData.calories * portions).toFixed(1),
          protein: (selectedFoodData.protein * portions).toFixed(1),
          carbs: (selectedFoodData.carbs * portions).toFixed(1),
          fat: (selectedFoodData.fat * portions).toFixed(1),
        };
      } else if (selectedFoodData.base_amount_unit === 'tablespoon') {
        const grams = convertToGrams(amountValue, unit);
        const tbspValue = grams / 15;
        const portions = tbspValue / selectedFoodData.base_amount;
        return {
          calories: (selectedFoodData.calories * portions).toFixed(1),
          protein: (selectedFoodData.protein * portions).toFixed(1),
          carbs: (selectedFoodData.carbs * portions).toFixed(1),
          fat: (selectedFoodData.fat * portions).toFixed(1),
        };
      }
    } else {
      // Old schema
      return calculateNutrition(
        selectedFoodData,
        convertToGrams(amountValue, unit),
      );
    }
    return null;
  })();

  return (
    <div style={styles.container}>
      <div style={styles.scrollView}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.profileSection}>
            <div style={styles.profileIcon} aria-hidden="true">
              <span style={styles.profileIconText}>💪</span>
            </div>
            <div style={styles.welcomeSection}>
              <div style={styles.welcomeText}>ברוכים הבאים</div>
              <div style={styles.userName}>{userNickname} ❤️</div>
            </div>
          </div>
        </div>

        {/* Title */}
        <div style={styles.titleSection}>
          <div style={styles.title}>הוסף רשומת מזון</div>
        </div>

        {/* Food Selection */}
        <div style={styles.card} className="card-hover">
          <div style={styles.label}>בחר מזון</div>
          {foods.length > 0 ? (
            <div style={styles.pickerContainer}>
              <select
                value={selectedFood || ''}
                onChange={e => setSelectedFood(Number(e.target.value))}
                style={styles.picker}
                aria-label="Select food">
                {foods.map(food => (
                  <option key={food.id} value={food.id} style={styles.option}>
                    {getFoodDisplayName(food)}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div style={styles.loadingContainer}>
              <div className="spinner-small" style={{marginBottom: 12}}></div>
              <div style={styles.loadingText}>טוען מזונות...</div>
            </div>
          )}
        </div>

        {/* Amount Input */}
        <div style={styles.card} className="card-hover">
          <div style={styles.label}>כמות</div>
          {selectedFoodData && isBasePortionFood(selectedFoodData) && selectedFoodData.base_amount_unit === 'unit' ? (
            <div style={styles.amountContainer}>
              <input
                style={styles.amountInput}
                type="number"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder={`הכנס מספר ${getHebrewUnitLabel('unit', 2)}`}
                aria-label="Amount of food"
                min="0"
                step="0.1"
              />
              <div style={styles.unitLabel}>
                {getHebrewUnitLabel('unit', parseFloat(amount) || 2)}
              </div>
            </div>
          ) : (
            <>
              <div style={styles.amountContainer}>
                <input
                  style={styles.amountInput}
                  type="number"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  placeholder="הכנס כמות"
                  aria-label="Amount of food"
                  min="0"
                  step="0.1"
                />
                <div style={styles.unitPickerContainer}>
                  <select
                    value={unit}
                    onChange={e => setUnit(e.target.value)}
                    style={styles.unitPicker}
                    aria-label="Select unit of measurement">
                    <option value="grams">גרם</option>
                    <option value="teaspoon">כפית (5גרם)</option>
                    <option value="tablespoon">כף (15גרם)</option>
                  </select>
                </div>
              </div>
              {amount && parseFloat(amount) > 0 && unit !== 'grams' && (
                <div style={styles.helperText}>
                  {amount} {getHebrewUnitLabel(unit, parseFloat(amount))} = {convertToGrams(parseFloat(amount), unit).toFixed(1)} {getHebrewUnitLabel('gram')}
                </div>
              )}
            </>
          )}
        </div>

        {/* Nutrition Preview */}
        {previewNutrition && (
          <div style={styles.card} className="card-hover">
            <div style={styles.previewTitle}>תצוגה מקדימה תזונתית</div>
            <div style={styles.nutritionGrid}>
              <div style={styles.nutritionItem}>
                <div style={{...styles.nutritionValue, color: colors.calories}}>
                  {previewNutrition.calories}
                </div>
                <div style={styles.nutritionLabel}>קלוריות</div>
              </div>
              <div style={styles.nutritionItem}>
                <div style={{...styles.nutritionValue, color: colors.protein}}>
                  {previewNutrition.protein}g
                </div>
                <div style={styles.nutritionLabel}>חלבון</div>
              </div>
              <div style={styles.nutritionItem}>
                <div style={{...styles.nutritionValue, color: colors.carbs}}>
                  {previewNutrition.carbs}g
                </div>
                <div style={styles.nutritionLabel}>פחמימות</div>
              </div>
              <div style={styles.nutritionItem}>
                <div style={{...styles.nutritionValue, color: colors.fat}}>
                  {previewNutrition.fat}g
                </div>
                <div style={styles.nutritionLabel}>שומן</div>
              </div>
            </div>
          </div>
        )}

        {/* Add Entry Button */}
        <button 
          style={styles.addButton} 
          onClick={handleAddEntry}
          className="btn-primary"
          aria-label="Add food entry to today's log"
          type="button">
          <span style={styles.addButtonText}>הוסף רשומה</span>
        </button>

        {/* Add Food Button */}
        <button
          style={styles.addFoodButton}
          onClick={() => setShowAddFoodModal(true)}
          className="btn-secondary"
          aria-label="Add new food to database"
          type="button">
          <span style={styles.addFoodButtonText}>➕ הוסף מזון למאגר</span>
        </button>

        {/* Add Food Modal */}
        {showAddFoodModal && (
          <div 
            style={styles.modalOverlay} 
            onClick={() => setShowAddFoodModal(false)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
            aria-describedby="modal-description">
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <div id="modal-title" style={styles.modalTitle}>הוסף מזון חדש</div>
                <button
                  style={styles.modalCloseButton}
                  onClick={() => setShowAddFoodModal(false)}
                  aria-label="Close add food modal"
                  type="button">
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              <div id="modal-description" style={styles.modalSubtitle}>
                הזן ערכים תזונתיים לפי כמות בסיס
              </div>

              <div style={styles.modalScrollContent}>
                <label htmlFor="food-name-input" style={{display: 'none'}}>שם המזון</label>
                <input
                  id="food-name-input"
                  style={styles.modalInput}
                  type="text"
                  placeholder="שם המזון"
                  value={newFoodName}
                  onChange={e => setNewFoodName(e.target.value)}
                  aria-label="Food name"
                  autoFocus
                />

                <div style={styles.modalRow}>
                  <input
                    style={{...styles.modalInput, flex: 1, marginRight: 8}}
                    type="number"
                    placeholder="כמות בסיס"
                    value={newFoodBaseAmount}
                    onChange={e => setNewFoodBaseAmount(e.target.value)}
                    min="0.1"
                    step="0.1"
                  />
                  <div style={{width: 180}}>
                    <select
                      style={styles.modalInput}
                      value={newFoodBaseAmountUnit}
                      onChange={e => setNewFoodBaseAmountUnit(e.target.value)}
                      aria-label="Base amount unit">
                      <option value="unit">יחידה</option>
                      <option value="gram">גרם</option>
                      <option value="tablespoon">כף</option>
                    </select>
                  </div>
                </div>

                <input
                  style={styles.modalInput}
                  type="number"
                  placeholder={`קלוריות (ל${newFoodBaseAmount} ${getHebrewUnitLabel(newFoodBaseAmountUnit, parseFloat(newFoodBaseAmount) || 1)})`}
                  value={newFoodCalories}
                  onChange={e => setNewFoodCalories(e.target.value)}
                  min="0"
                  step="0.1"
                  aria-label="Calories"
                />

                <input
                  style={styles.modalInput}
                  type="number"
                  placeholder={`חלבון (גרם) (ל${newFoodBaseAmount} ${getHebrewUnitLabel(newFoodBaseAmountUnit, parseFloat(newFoodBaseAmount) || 1)})`}
                  value={newFoodProtein}
                  onChange={e => setNewFoodProtein(e.target.value)}
                  min="0"
                  step="0.1"
                  aria-label="Protein in grams"
                />

                <input
                  style={styles.modalInput}
                  type="number"
                  placeholder={`פחמימות (גרם) (ל${newFoodBaseAmount} ${getHebrewUnitLabel(newFoodBaseAmountUnit, parseFloat(newFoodBaseAmount) || 1)})`}
                  value={newFoodCarbs}
                  onChange={e => setNewFoodCarbs(e.target.value)}
                  min="0"
                  step="0.1"
                  aria-label="Carbohydrates in grams"
                />

                <input
                  style={styles.modalInput}
                  type="number"
                  placeholder={`שומן (גרם) (ל${newFoodBaseAmount} ${getHebrewUnitLabel(newFoodBaseAmountUnit, parseFloat(newFoodBaseAmount) || 1)})`}
                  value={newFoodFat}
                  onChange={e => setNewFoodFat(e.target.value)}
                  min="0"
                  step="0.1"
                  aria-label="Fat in grams"
                />
              </div>

              <div style={styles.modalButtons}>
                <button
                  style={{...styles.modalButton, ...styles.cancelButton}}
                  onClick={() => {
                    setShowAddFoodModal(false);
                    setNewFoodName('');
                    setNewFoodCalories('');
                    setNewFoodProtein('');
                    setNewFoodCarbs('');
                    setNewFoodFat('');
                    setNewFoodBaseAmount('1');
                    setNewFoodBaseAmountUnit('unit');
                  }}
                  className="btn-secondary"
                  aria-label="Cancel adding food">
                  <span style={{...styles.modalButtonText, color: colors.textOnPrimary}}>
                    ביטול
                  </span>
                </button>
                <button
                  style={{...styles.modalButton, ...styles.saveButton}}
                  onClick={handleAddFood}
                  className="btn-primary"
                  aria-label="Save new food">
                  <span style={styles.modalButtonText}>שמור</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    flex: 1,
    backgroundColor: colors.bg,
    minHeight: '100vh',
  },
  scrollView: {
    flex: 1,
    paddingBottom: 20,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  loadingText: {
    color: colors.textPrimary,
    fontSize: 16,
    fontWeight: 500,
  },
  header: {
    paddingLeft: 20,
    paddingRight: 20,
    paddingTop: 16,
    paddingBottom: 24,
  },
  profileSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  profileIcon: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: colors.surface,
    border: `2px solid ${colors.primary}`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 15,
  },
  profileIconText: {
    fontSize: 30,
  },
  welcomeSection: {
    flex: 1,
  },
  welcomeText: {
    color: colors.textTertiary,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.5,
  },
  userName: {
    color: colors.textPrimary,
    fontSize: 24,
    fontWeight: 'bold',
    marginTop: 4,
    lineHeight: 1.2,
  },
  titleSection: {
    paddingLeft: 20,
    paddingRight: 20,
    marginBottom: 32,
  },
  title: {
    fontSize: 32,
    fontWeight: 'bold',
    color: colors.textPrimary,
    lineHeight: 1.2,
    letterSpacing: -0.5,
  },
  card: {
    backgroundColor: colors.surface,
    marginLeft: 20,
    marginRight: 20,
    marginBottom: 16,
    padding: 24,
    borderRadius: 16,
    border: `1px solid ${colors.border}`,
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    transition: 'all 200ms ease',
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 16,
    color: colors.textPrimary,
    lineHeight: 1.5,
  },
  pickerContainer: {
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: colors.surface,
    transition: 'all 200ms ease',
  },
  picker: {
    width: '100%',
    height: 52,
    color: colors.textPrimary,
    backgroundColor: colors.surface,
    border: 'none',
    padding: '14px 16px',
    fontSize: 16,
    outline: 'none',
    cursor: 'pointer',
    fontWeight: 500,
  },
  option: {
    backgroundColor: colors.surface,
    color: colors.textPrimary,
  },
  amountContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  amountInput: {
    flex: 1,
    height: 52,
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    padding: '14px 16px',
    fontSize: 16,
    backgroundColor: colors.surface,
    color: colors.textPrimary,
    transition: 'all 200ms ease',
    fontWeight: 500,
  },
  unitPickerContainer: {
    width: 160,
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: colors.surface,
    transition: 'all 200ms ease',
  },
  unitPicker: {
    width: '100%',
    height: 52,
    color: colors.textPrimary,
    backgroundColor: colors.surface,
    border: 'none',
    padding: '14px 16px',
    fontSize: 16,
    outline: 'none',
    cursor: 'pointer',
    fontWeight: 500,
  },
  unitLabel: {
    width: 160,
    height: 52,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.textTertiary,
    fontSize: 16,
    fontWeight: 500,
    padding: '0 16px',
    backgroundColor: colors.bg,
    borderRadius: 12,
    border: `1px solid ${colors.border}`,
  },
  helperText: {
    fontSize: 12,
    color: colors.textTertiary,
    marginTop: 12,
    fontStyle: 'italic',
    lineHeight: 1.5,
  },
  previewTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginBottom: 20,
    color: colors.textPrimary,
    lineHeight: 1.5,
  },
  nutritionGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  nutritionItem: {
    width: '48%',
    backgroundColor: colors.bg,
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${colors.border}`,
    transition: 'all 200ms ease',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  nutritionValue: {
    fontSize: 22,
    fontWeight: 'bold',
    lineHeight: 1.2,
    marginBottom: 4,
    transition: 'color 200ms ease',
  },
  nutritionLabel: {
    fontSize: 12,
    color: colors.textTertiary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontWeight: 500,
  },
  addButton: {
    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
    marginLeft: 20,
    marginRight: 20,
    marginTop: 16,
    marginBottom: 16,
    height: 56,
    padding: '16px 24px',
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 'calc(100% - 40px)',
    cursor: 'pointer',
    boxShadow: `0 4px 12px ${colors.shadowPrimary}`,
    transition: 'all 200ms ease',
    border: 'none',
  },
  addButtonText: {
    color: colors.textOnPrimary,
    fontSize: 16,
    fontWeight: '600',
    letterSpacing: 0.3,
  },
  addFoodButton: {
    backgroundColor: colors.surface,
    marginLeft: 20,
    marginRight: 20,
    marginBottom: 32,
    height: 48,
    padding: '12px 20px',
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 'calc(100% - 40px)',
    border: `1px solid ${colors.border}`,
    cursor: 'pointer',
    transition: 'all 200ms ease',
  },
  addFoodButtonText: {
    color: colors.primary,
    fontSize: 16,
    fontWeight: '600',
    letterSpacing: 0.3,
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.overlay,
    backdropFilter: 'blur(4px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    zIndex: 1000,
    animation: 'fadeIn 200ms ease',
    padding: '10px',
    paddingBottom: '80px',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  modalContent: {
    backgroundColor: colors.surface,
    width: '100%',
    maxWidth: 500,
    padding: '20px',
    borderRadius: 20,
    border: `1px solid ${colors.border}`,
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    animation: 'slideIn 300ms ease',
    marginTop: '10px',
    marginBottom: '10px',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 'calc(100vh - 100px)',
    minHeight: '300px',
    position: 'relative',
  },
  modalHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
    flexShrink: 0,
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.textPrimary,
    lineHeight: 1.2,
    flex: 1,
  },
  modalCloseButton: {
    background: 'none',
    border: 'none',
    color: colors.textPrimary,
    fontSize: 32,
    cursor: 'pointer',
    padding: 0,
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    opacity: 0.7,
    transition: 'opacity 200ms ease',
  },
  modalSubtitle: {
    fontSize: 14,
    color: colors.textTertiary,
    marginBottom: 16,
    lineHeight: 1.5,
    flexShrink: 0,
  },
  modalScrollContent: {
    flex: '1 1 auto',
    overflowY: 'auto',
    overflowX: 'hidden',
    marginBottom: 16,
    paddingRight: 4,
    minHeight: 0,
    WebkitOverflowScrolling: 'touch',
  },
  modalRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    gap: 8,
  },
  modalInput: {
    width: '100%',
    height: 48,
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    padding: '12px 16px',
    fontSize: 16,
    marginBottom: 12,
    backgroundColor: colors.bg,
    color: colors.textPrimary,
    boxSizing: 'border-box',
    transition: 'all 200ms ease',
    fontWeight: 500,
    outline: 'none',
  },
  modalButtons: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
    paddingTop: 16,
    borderTop: `1px solid ${colors.border}`,
    flexShrink: 0,
    marginTop: 'auto',
  },
  modalButton: {
    flex: 1,
    height: 48,
    padding: '12px 20px',
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    border: 'none',
    transition: 'all 200ms ease',
  },
  cancelButton: {
    backgroundColor: colors.surfaceAlt,
    color: colors.textPrimary,
  },
  saveButton: {
    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
    color: colors.textOnPrimary,
    boxShadow: `0 4px 12px ${colors.shadowPrimary}`,
  },
  modalButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.textOnPrimary,
    letterSpacing: 0.3,
  },
};

export default HomeScreen;
